import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, TabStopType, TabStopPosition, convertInchesToTwip, ExternalHyperlink } from "docx";
import { saveAs } from "file-saver";
import { ResumeData, ResumeFormat } from "@/types/resume";
import { formatDateFull, sanitizeText } from "./resumeUtils";
import { SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";

// American standard resume format specifications
// Letter page: 8.5" x 11" = 612pt x 792pt
// Name: 21pt, Section headers: 12pt, Subheaders: 11pt, Body: 10pt
// Section spacing: 2pt, Entry spacing: 0pt, Line spacing: 12pt
// Top/bottom margin: 26pt, Side margins: 36pt

const sanitizeFileName = (name: string) => {
  return name
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .slice(0, 80);
};

export const generatePDF = (
  data: ResumeData,
  format: ResumeFormat = "standard",
  fileNameOverride?: string,
  sectionVisibility: SectionVisibility = defaultSectionVisibility,
  sectionOrder: SectionKey[] = DEFAULT_SECTION_ORDER
): void => {
  const doc = new jsPDF({
    unit: "pt",
    format: "letter", // 8.5" x 11"
  });

  // Margins - tighter for better content fit
  const marginTop = 29; // ~0.4in
  const marginBottom = 29;
  const marginSide = 40; // ~0.55in
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginSide * 2;
  
  // Font sizes
  const nameSize = format === 'compact' ? 18 : 21;
  const sectionSize = 12;
  const subheaderSize = 11;
  const bodySize = 10;
  const lineHeight = 13; // slightly increased for readability
  // Increased spacing between sections and entries for a more breathable layout
  const sectionSpacing = format === 'compact' ? 4 : 8;
  const entrySpacing = format === 'compact' ? 4 : 6;

  let yPos = marginTop;

  // Helper to check page break
  const checkPageBreak = (neededHeight: number = lineHeight) => {
    if (yPos + neededHeight > pageHeight - marginBottom) {
      doc.addPage();
      yPos = marginTop;
    }
  };

  // Helper to add justified text (mimics text-align: justify)
  const addJustifiedText = (text: string, x: number, maxWidth: number) => {
    const words = text.split(' ').filter(w => w.length > 0);
    if (words.length <= 1) {
      doc.text(text, x, yPos);
      return;
    }
    
    // Check if this line fits or needs wrapping
    const fullWidth = doc.getTextWidth(text);
    if (fullWidth <= maxWidth) {
      // Single line - justify by adding space between words
      const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
      const totalSpaceNeeded = maxWidth - totalWordWidth;
      const spacePerGap = totalSpaceNeeded / (words.length - 1);
      
      let currentX = x;
      words.forEach((word, idx) => {
        doc.text(word, currentX, yPos);
        currentX += doc.getTextWidth(word) + spacePerGap;
      });
    } else {
      // Just use normal text for wrapped lines
      doc.text(text, x, yPos);
    }
  };

  // Helper to add wrapped justified bullet text
  const addJustifiedBullet = (bullet: string, bulletX: number, textX: number, maxWidth: number) => {
    doc.text("•", bulletX, yPos);
    const lines = doc.splitTextToSize(bullet, maxWidth);
    lines.forEach((line: string, idx: number) => {
      checkPageBreak();
      if (idx === lines.length - 1) {
        // Last line - left align
        doc.text(line, textX, yPos);
      } else {
        // Other lines - justify
        addJustifiedText(line, textX, maxWidth);
      }
      yPos += lineHeight;
    });
  };

  // Helper to add wrapped text
  const addWrappedText = (text: string, fontSize: number, isBold = false, indent = 0) => {
    doc.setFontSize(fontSize);
    doc.setFont("times", isBold ? "bold" : "normal");
    const lines = doc.splitTextToSize(text, contentWidth - indent);
    lines.forEach((line: string) => {
      checkPageBreak();
      doc.text(line, marginSide + indent, yPos);
      yPos += lineHeight;
    });
  };

  // Section header with black underline (no overlap with body text)
  const addSectionHeader = (title: string) => {
    yPos += sectionSpacing;
    doc.setFontSize(sectionSize);
    doc.setFont("times", "bold");

    // Keep extra room so underline never collides with the next paragraph
    checkPageBreak(lineHeight * 2);

    const headerY = yPos;
    doc.text(title.toUpperCase(), marginSide, headerY);

    const underlineY = headerY + 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(marginSide, underlineY, pageWidth - marginSide, underlineY);

    // Start next text safely below the underline
    yPos = underlineY + lineHeight;
  };

  // Header - Name (centered)
  doc.setFontSize(nameSize);
  doc.setFont("times", "bold");
  doc.text(data.personalInfo.fullName.toUpperCase(), pageWidth / 2, yPos, { align: "center" });
  yPos += nameSize * 0.6;

  // Contact info (centered) - clickable labels for links
  const normalizeUrl = (url: string | undefined | null): string | null => {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;

    // Remove accidental whitespace inside URLs (common copy/paste issue)
    const noSpaces = trimmed.replace(/\s+/g, "");
    const normalized = noSpaces.startsWith("http") ? noSpaces : `https://${noSpaces}`;

    try {
      // Validate URL; if invalid, skip link rather than breaking export
      // eslint-disable-next-line no-new
      new URL(normalized);
      return normalized;
    } catch {
      return null;
    }
  };

  const contactSegments: Array<{ text: string; url?: string }> = [];
  if (data.personalInfo.location) contactSegments.push({ text: data.personalInfo.location });
  if (data.personalInfo.phone) contactSegments.push({ text: data.personalInfo.phone });
  if (data.personalInfo.email) contactSegments.push({ text: data.personalInfo.email });

  const githubUrl = normalizeUrl(data.personalInfo.github);
  if (githubUrl) contactSegments.push({ text: "GitHub", url: githubUrl });

  const portfolioUrl = normalizeUrl(data.personalInfo.portfolio);
  if (portfolioUrl) contactSegments.push({ text: "Portfolio", url: portfolioUrl });

  const linkedinUrl = normalizeUrl(data.personalInfo.linkedin);
  if (linkedinUrl) contactSegments.push({ text: "LinkedIn", url: linkedinUrl });

  doc.setFontSize(bodySize);
  doc.setFont("times", "normal");

  const separator = " | ";
  const sepWidth = doc.getTextWidth(separator);
  const totalWidth =
    contactSegments.reduce((sum, seg) => sum + doc.getTextWidth(seg.text), 0) +
    (contactSegments.length > 1 ? sepWidth * (contactSegments.length - 1) : 0);

  const addPdfLink = (text: string, url: string, x: number, y: number) => {
    const anyDoc = doc as unknown as {
      textWithLink?: (t: string, xx: number, yy: number, opts: { url: string }) => void;
      link?: (xx: number, yy: number, w: number, h: number, opts: { url: string }) => void;
    };

    if (typeof anyDoc.textWithLink === "function") {
      anyDoc.textWithLink(text, x, y, { url });
      return;
    }

    // Fallback: draw text + annotate link if supported
    doc.text(text, x, y);
    if (typeof anyDoc.link === "function") {
      const w = doc.getTextWidth(text);
      anyDoc.link(x, y - bodySize, w, lineHeight, { url });
    }
  };

  let xPos = (pageWidth - totalWidth) / 2;
  contactSegments.forEach((seg, idx) => {
    if (idx > 0) {
      doc.text(separator, xPos, yPos);
      xPos += sepWidth;
    }

    if (seg.url) {
      addPdfLink(seg.text, seg.url, xPos, yPos);
    } else {
      doc.text(seg.text, xPos, yPos);
    }

    xPos += doc.getTextWidth(seg.text);
  });

  yPos += lineHeight + sectionSpacing;

  // Section renderers
  const renderPdfEducation = () => {
    if (!(sectionVisibility.education && data.education.length > 0)) return;
    addSectionHeader("Education");
    data.education.forEach((edu) => {
      checkPageBreak(lineHeight * 2);
      doc.setFontSize(subheaderSize);
      doc.setFont("times", "bold");
      doc.text(edu.institution, marginSide, yPos);
      if (edu.location) {
        doc.setFontSize(bodySize);
        doc.text(edu.location, pageWidth - marginSide, yPos, { align: "right" });
      }
      yPos += lineHeight;
      doc.setFontSize(bodySize);
      doc.setFont("times", "normal");
      let degreeText = edu.degree;
      if (edu.field) degreeText += ` in ${edu.field}`;
      if (edu.gpa) degreeText += ` | GPA: ${edu.gpa}`;
      doc.text(degreeText, marginSide, yPos);
      if (edu.graduationDate) {
        doc.setFont("times", "bold");
        doc.text(formatDateFull(edu.graduationDate), pageWidth - marginSide, yPos, { align: "right" });
        doc.setFont("times", "normal");
      }
      yPos += lineHeight + sectionSpacing;
    });
  };

  const renderPdfSummary = () => {
    if (!(sectionVisibility.summary && data.summary)) return;
    addSectionHeader("Summary");
    addWrappedText(data.summary, bodySize);
    yPos += sectionSpacing;
  };

  const renderPdfSkills = () => {
    if (!(sectionVisibility.skills && data.coreStrengths && data.coreStrengths.length > 0 && data.coreStrengths.some(c => c.category && c.skills))) return;
    addSectionHeader("Skills");
    data.coreStrengths.filter(c => c.category && c.skills).forEach((cat) => {
      checkPageBreak();
      doc.setFontSize(bodySize);
      doc.setFont("times", "bold");
      const categoryText = `• ${sanitizeText(cat.category)}: `;
      doc.text(categoryText, marginSide, yPos);
      const categoryWidth = doc.getTextWidth(categoryText);
      doc.setFont("times", "normal");
      const sanitizedSkills = sanitizeText(cat.skills);
      const skillLines = doc.splitTextToSize(sanitizedSkills, contentWidth - categoryWidth);
      doc.text(skillLines[0], marginSide + categoryWidth, yPos);
      yPos += lineHeight;
      if (skillLines.length > 1) {
        for (let i = 1; i < skillLines.length; i++) {
          checkPageBreak();
          doc.text(skillLines[i], marginSide + 12, yPos);
          yPos += lineHeight;
        }
      }
    });
    yPos += sectionSpacing;
  };

  const renderPdfExperience = () => {
    if (!(sectionVisibility.experience && data.experience.length > 0)) return;
    addSectionHeader("Experience");
    data.experience.forEach((exp) => {
      checkPageBreak(lineHeight * 3);
      const startFormatted = formatDateFull(exp.startDate);
      const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
      const hasDateRange = Boolean(startFormatted || endFormatted);
      const dateDisplay = hasDateRange
        ? `${startFormatted || ""}${startFormatted && endFormatted ? " - " : ""}${endFormatted || ""}`
        : "";
      doc.setFontSize(subheaderSize);
      doc.setFont("times", "bold");
      doc.text(exp.company, marginSide, yPos);
      if (dateDisplay) {
        doc.setFontSize(bodySize);
        doc.text(dateDisplay, pageWidth - marginSide, yPos, { align: "right" });
      }
      yPos += lineHeight;
      doc.setFontSize(bodySize);
      doc.setFont("times", "bold");
      doc.text(exp.jobTitle, marginSide, yPos);
      const locationDisplay = [exp.location, exp.workType].filter(Boolean).join(" | ");
      if (locationDisplay) {
        doc.text(locationDisplay, pageWidth - marginSide, yPos, { align: "right" });
      }
      yPos += lineHeight;
      doc.setFont("times", "normal");
      const bulletIndent = 8;
      const textIndent = 16;
      (exp.bullets || []).filter(Boolean).forEach((bullet) => {
        checkPageBreak();
        addJustifiedBullet(bullet, marginSide + bulletIndent, marginSide + textIndent, contentWidth - textIndent - bulletIndent);
      });
      yPos += sectionSpacing;
    });
  };

  // Helper for custom section items
  const renderCustomSectionItems = (section: any, isProjectSection: boolean = false) => {
    (section.items || []).forEach((item: any) => {
      if (item.type === 'bullet') {
        if (item.title) {
          checkPageBreak();
          doc.setFontSize(bodySize);
          doc.setFont("times", "normal");
          addJustifiedBullet(item.title, marginSide + 8, marginSide + 16, contentWidth - 24);
        }
        return;
      }
      const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
      const _a = (item.title || "").toLowerCase().trim();
      const _b = (section.title || "").toLowerCase().trim();
      const showTitle = item.title && (isProjectSection || !(_a === _b || _a.startsWith(_b) || _b.startsWith(_a)));
      if (hasContent) {
        checkPageBreak(lineHeight * 2);
        if (showTitle) {
          doc.setFontSize(isProjectSection ? subheaderSize : bodySize);
          doc.setFont("times", isProjectSection ? "bold" : "normal");
          if (isProjectSection && item.link) {
            const projectLinkUrl = normalizeUrl(item.link);
            doc.text(item.title, marginSide, yPos);
            if (projectLinkUrl) {
              const titleWidth = doc.getTextWidth(item.title);
              doc.setFont("times", "normal");
              doc.setFontSize(bodySize);
              doc.text(" | ", marginSide + titleWidth, yPos);
              const separatorWidth = doc.getTextWidth(" | ");
              addPdfLink("Project Link", projectLinkUrl, marginSide + titleWidth + separatorWidth, yPos);
            }
            if (item.date) {
              doc.setFontSize(bodySize);
              doc.setFont("times", "normal");
              doc.text(item.date, pageWidth - marginSide, yPos, { align: "right" });
            }
            yPos += lineHeight;
          } else {
            doc.text(item.title, marginSide, yPos);
            if (item.date) {
              doc.setFontSize(bodySize);
              doc.setFont("times", "normal");
              doc.text(item.date, pageWidth - marginSide, yPos, { align: "right" });
            }
            yPos += lineHeight;
            const linkUrl = normalizeUrl(item.link);
            if (linkUrl) {
              checkPageBreak();
              doc.setFontSize(bodySize);
              doc.setFont("times", "normal");
              addPdfLink("View Credential", linkUrl, marginSide, yPos);
              yPos += lineHeight;
            }
          }
        }
        if (item.subtitle) {
          doc.setFontSize(bodySize);
          doc.setFont("times", "italic");
          doc.text(item.subtitle, marginSide, yPos);
          yPos += lineHeight;
        }
        if (item.description) {
          doc.setFont("times", "normal");
          addWrappedText(item.description, bodySize);
        }
        doc.setFont("times", "normal");
        const customBulletIndent = 8;
        const customTextIndent = 16;
        (item.bullets || []).filter(Boolean).forEach((bullet: string) => {
          checkPageBreak();
          addJustifiedBullet(bullet, marginSide + customBulletIndent, marginSide + customTextIndent, contentWidth - customTextIndent - customBulletIndent);
        });
        yPos += 3;
      }
    });
  };

  const renderPdfCustom = () => {
    if (!sectionVisibility.customSections) return;
    const certSections = (data.customSections || []).filter((s) => /certif|licen/i.test(s.title || ""));
    certSections.forEach((section) => { addSectionHeader(section.title); renderCustomSectionItems(section); });
    const projSections = (data.customSections || []).filter((s) => /project/i.test(s.title || ""));
    projSections.forEach((section) => { addSectionHeader(section.title); renderCustomSectionItems(section, true); });
    const otherSections = (data.customSections || []).filter((s) => s.title && !/certif|licen|project/i.test(s.title));
    otherSections.forEach((section) => { addSectionHeader(section.title); renderCustomSectionItems(section, false); });
  };

  // Render sections in user-defined order
  const pdfSectionRenderers: Record<string, () => void> = {
    education: renderPdfEducation,
    summary: renderPdfSummary,
    skills: renderPdfSkills,
    experience: renderPdfExperience,
    custom: renderPdfCustom,
  };

  sectionOrder
    .filter(key => key !== "personal")
    .forEach(key => pdfSectionRenderers[key]?.());

  // Save
  const base = sanitizeFileName(fileNameOverride || `${data.personalInfo.fullName} Resume`);
  const fileName = `${base}.pdf`;
  doc.save(fileName);
};

export const generateDOCX = async (
  data: ResumeData,
  format: ResumeFormat = "standard",
  fileNameOverride?: string,
  sectionVisibility: SectionVisibility = defaultSectionVisibility,
  sectionOrder: SectionKey[] = DEFAULT_SECTION_ORDER
): Promise<void> => {
  const children: Paragraph[] = [];
  const blackBorder = { bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" } };
  
  // Font sizes in half-points (multiply pt by 2)
  const nameSize = format === 'compact' ? 36 : 42; // 18pt or 21pt
  const sectionSize = 24; // 12pt
  const subheaderSize = 22; // 11pt
  const bodySize = 20; // 10pt
  // Increased spacing for a more breathable layout (values in twentieths of a point, twips)
  const sectionSpacing = format === 'compact' ? 60 : 100;
  const entrySpacing = format === 'compact' ? 40 : 80;

  // Bullet paragraphs: use hanging indent so wrapped lines never sit under the bullet symbol.
  // Use JUSTIFIED alignment for professional look
  const bulletIndent = { left: 270, hanging: 90 };
  const addBulletParagraph = (
    runs: Array<TextRun | ExternalHyperlink>,
    spacingAfter: number = 0
  ) => {
    children.push(
      new Paragraph({
        children: runs,
        indent: bulletIndent,
        spacing: { after: spacingAfter },
        alignment: AlignmentType.JUSTIFIED,
      })
    );
  };

  // Right tab stop aligned to the usable content width (8.5" - 0.55" margins = 7.4")
  const rightTabPos = convertInchesToTwip(7.4);

  // Name
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: data.personalInfo.fullName.toUpperCase(),
          bold: true,
          size: nameSize,
          font: "Times New Roman",
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 40 },
    })
  );

  // Contact info - clickable labels for links
  const normalizeUrl = (url: string | undefined | null): string | null => {
    if (!url) return null;
    const trimmed = url.trim();
    if (!trimmed) return null;

    const noSpaces = trimmed.replace(/\s+/g, "");
    const normalized = noSpaces.startsWith("http") ? noSpaces : `https://${noSpaces}`;

    try {
      // eslint-disable-next-line no-new
      new URL(normalized);
      return normalized;
    } catch {
      return null;
    }
  };

  const contactSegments: Array<{ text: string; url?: string }> = [];
  if (data.personalInfo.location) contactSegments.push({ text: data.personalInfo.location });
  if (data.personalInfo.phone) contactSegments.push({ text: data.personalInfo.phone });
  if (data.personalInfo.email) contactSegments.push({ text: data.personalInfo.email });

  const githubUrl = normalizeUrl(data.personalInfo.github);
  if (githubUrl) contactSegments.push({ text: "GitHub", url: githubUrl });

  const portfolioUrl = normalizeUrl(data.personalInfo.portfolio);
  if (portfolioUrl) contactSegments.push({ text: "Portfolio", url: portfolioUrl });

  const linkedinUrl = normalizeUrl(data.personalInfo.linkedin);
  if (linkedinUrl) contactSegments.push({ text: "LinkedIn", url: linkedinUrl });

  const contactChildren: Array<TextRun | ExternalHyperlink> = [];
  contactSegments.forEach((seg, idx) => {
    if (idx > 0) {
      contactChildren.push(new TextRun({ text: " | ", size: bodySize, font: "Times New Roman" }));
    }

    if (seg.url) {
      contactChildren.push(
        new ExternalHyperlink({
          link: seg.url,
          children: [
            new TextRun({
              text: seg.text,
              style: "Hyperlink",
              size: bodySize,
              font: "Times New Roman",
            }),
          ],
        })
      );
    } else {
      contactChildren.push(new TextRun({ text: seg.text, size: bodySize, font: "Times New Roman" }));
    }
  });

  children.push(
    new Paragraph({
      children: contactChildren,
      alignment: AlignmentType.CENTER,
      spacing: { after: sectionSpacing * 2 },
    })
  );

  // Section helper
  const addSectionHeader = (title: string) => {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: title.toUpperCase(),
            bold: true,
            size: sectionSize,
            font: "Times New Roman",
          }),
        ],
        border: blackBorder,
        spacing: { before: sectionSpacing * 2, after: sectionSpacing },
      })
    );
  };

  // DOCX section renderers
  const renderDocxEducation = () => {
    if (!(sectionVisibility.education && data.education.length > 0)) return;
    addSectionHeader("Education");
    data.education.forEach((edu) => {
      children.push(new Paragraph({
        children: [
          new TextRun({ text: edu.institution, bold: true, size: subheaderSize, font: "Times New Roman" }),
          new TextRun({ text: edu.location ? `\t${edu.location}` : "", bold: true, size: bodySize, font: "Times New Roman" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
      }));
      let degreeText = edu.degree;
      if (edu.field) degreeText += ` in ${edu.field}`;
      if (edu.gpa) degreeText += ` | GPA: ${edu.gpa}`;
      children.push(new Paragraph({
        children: [
          new TextRun({ text: degreeText, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: edu.graduationDate ? `\t${formatDateFull(edu.graduationDate)}` : "", bold: true, size: bodySize, font: "Times New Roman" }),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
        spacing: { after: sectionSpacing },
      }));
    });
  };

  const renderDocxSummary = () => {
    if (!(sectionVisibility.summary && data.summary)) return;
    addSectionHeader("Summary");
    children.push(new Paragraph({
      children: [new TextRun({ text: data.summary, size: bodySize, font: "Times New Roman" })],
      spacing: { after: sectionSpacing },
    }));
  };

  const renderDocxSkills = () => {
    if (!(sectionVisibility.skills && data.coreStrengths && data.coreStrengths.length > 0 && data.coreStrengths.some(c => c.category && c.skills))) return;
    addSectionHeader("Skills");
    data.coreStrengths.filter(c => c.category && c.skills).forEach((cat, idx, arr) => {
      addBulletParagraph(
        [
          new TextRun({ text: "• ", size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: `${sanitizeText(cat.category)}: `, bold: true, size: bodySize, font: "Times New Roman" }),
          new TextRun({ text: sanitizeText(cat.skills), size: bodySize, font: "Times New Roman" }),
        ],
        idx === arr.length - 1 ? entrySpacing : 30
      );
    });
  };

  const renderDocxExperience = () => {
    if (!(sectionVisibility.experience && data.experience.length > 0)) return;
    addSectionHeader("Experience");
    data.experience.forEach((exp) => {
      const startFormatted = formatDateFull(exp.startDate);
      const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
      const hasDateRange = Boolean(startFormatted || endFormatted);
      const dateDisplay = hasDateRange ? `${startFormatted || ""}${startFormatted && endFormatted ? " - " : ""}${endFormatted || ""}` : "";
      const locationDisplay = [exp.location, exp.workType].filter(Boolean).join(" | ");
      const bullets = (exp.bullets || []).filter(Boolean);
      children.push(new Paragraph({
        children: [
          new TextRun({ text: exp.company, bold: true, size: subheaderSize, font: "Times New Roman" }),
          ...(dateDisplay ? [new TextRun({ text: `\t${dateDisplay}`, bold: true, size: bodySize, font: "Times New Roman" })] : []),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
        spacing: { before: 0, after: 0 },
      }));
      children.push(new Paragraph({
        children: [
          new TextRun({ text: exp.jobTitle, bold: true, size: bodySize, font: "Times New Roman" }),
          ...(locationDisplay ? [new TextRun({ text: `\t${locationDisplay}`, bold: true, size: bodySize, font: "Times New Roman" })] : []),
        ],
        tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
        spacing: { before: 0, after: bullets.length > 0 ? 0 : entrySpacing },
      }));
      bullets.forEach((bullet, idx) => {
        addBulletParagraph(
          [new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })],
          idx === bullets.length - 1 ? entrySpacing : 0
        );
      });
    });
  };

  const renderDocxCustomSection = (section: any, isProjectSection: boolean = false) => {
    addSectionHeader(section.title);
    (section.items || []).forEach((item: any) => {
      if (item.type === 'bullet') {
        if (item.title) {
          addBulletParagraph([new TextRun({ text: `• ${item.title}`, size: bodySize, font: "Times New Roman" })], 0);
        }
        return;
      }
      const hasContent = item.title || item.description || (item.bullets || []).filter(Boolean).length > 0;
      const _a = (item.title || "").toLowerCase().trim();
      const _b = (section.title || "").toLowerCase().trim();
      const showTitle = item.title && (isProjectSection || !(_a === _b || _a.startsWith(_b) || _b.startsWith(_a)));
      if (hasContent) {
        if (showTitle) {
          const projectLinkUrl = normalizeUrl(item.link);
          if (isProjectSection && projectLinkUrl) {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: item.title, bold: true, size: subheaderSize, font: "Times New Roman" }),
                new TextRun({ text: " | ", size: bodySize, font: "Times New Roman" }),
                new ExternalHyperlink({ link: projectLinkUrl, children: [new TextRun({ text: "Project Link", style: "Hyperlink", size: bodySize, font: "Times New Roman" })] }),
                new TextRun({ text: item.date ? `\t${item.date}` : "", size: bodySize, font: "Times New Roman" }),
              ],
              tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
            }));
          } else {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: item.title, bold: isProjectSection, size: isProjectSection ? subheaderSize : bodySize, font: "Times New Roman" }),
                new TextRun({ text: item.date ? `\t${item.date}` : "", size: bodySize, font: "Times New Roman" }),
              ],
              tabStops: [{ type: TabStopType.RIGHT, position: rightTabPos }],
            }));
            if (projectLinkUrl && !isProjectSection) {
              children.push(new Paragraph({
                children: [new ExternalHyperlink({ link: projectLinkUrl, children: [new TextRun({ text: "View Credential", style: "Hyperlink", size: bodySize, font: "Times New Roman" })] })],
              }));
            }
          }
        }
        if (item.subtitle) {
          children.push(new Paragraph({ children: [new TextRun({ text: item.subtitle, italics: true, size: bodySize, font: "Times New Roman" })] }));
        }
        if (item.description) {
          children.push(new Paragraph({ children: [new TextRun({ text: item.description, size: bodySize, font: "Times New Roman" })] }));
        }
        (item.bullets || []).filter(Boolean).forEach((bullet: string) => {
          addBulletParagraph([new TextRun({ text: `• ${bullet}`, size: bodySize, font: "Times New Roman" })], 0);
        });
        children.push(new Paragraph({ spacing: { after: 40 } }));
      }
    });
  };

  const renderDocxCustom = () => {
    if (!sectionVisibility.customSections) return;
    const certSections = (data.customSections || []).filter((s) => /certif|licen/i.test(s.title || ""));
    certSections.forEach((section) => renderDocxCustomSection(section, false));
    const projSections = (data.customSections || []).filter((s) => /project/i.test(s.title || ""));
    projSections.forEach((section) => renderDocxCustomSection(section, true));
    const otherSections = (data.customSections || []).filter((s) => s.title && !/certif|licen|project/i.test(s.title));
    otherSections.forEach((section) => renderDocxCustomSection(section, false));
  };

  // Render sections in user-defined order
  const docxRenderers: Record<string, () => void> = {
    education: renderDocxEducation,
    summary: renderDocxSummary,
    skills: renderDocxSkills,
    experience: renderDocxExperience,
    custom: renderDocxCustom,
  };

  sectionOrder
    .filter(key => key !== "personal")
    .forEach(key => docxRenderers[key]?.());

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.4),
              bottom: convertInchesToTwip(0.4),
              left: convertInchesToTwip(0.55),
              right: convertInchesToTwip(0.55),
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const base = sanitizeFileName(fileNameOverride || `${data.personalInfo.fullName} Resume`);
  const fileName = `${base}.docx`;
  saveAs(blob, fileName);
};
